import React, { useState } from "react";
import { Check, X } from "lucide-react";
import { handleGetAppLogo } from "../../../utils/constants/GlobalAppAndLanguage";
import useAmsConfig from "../../../hooks/useAmsConfig";
import useGlobalAppAndLanguage from "../../../hooks/useGlobalAppAndLanguage";
import useAppSwitchNavigation from "../../../hooks/useAppSwitchNavigation";
import CustomModal from "../CustomModal";

/** Pick the app and language the whole AMS works in. */
const AppSwitchModal = ({ open, onClose }) => {
  const {
    globalApp,
    globalLanguage,
    handleResolveLanguage,
    handleGlobalLabel,
    lastLanguageByApp,
  } = useGlobalAppAndLanguage();
  const { handleSwitchAndNavigate } = useAppSwitchNavigation();
  const [draft, setDraft] = useState({
    app: globalApp,
    language: globalLanguage,
    picks: { ...lastLanguageByApp, [globalApp]: globalLanguage },
  });
  const { appNames, languagesFor } = useAmsConfig();
  const languageOptions = languagesFor(draft.app);
  const isDirty = draft.app !== globalApp || draft.language !== globalLanguage;

  const handleSelectApp = ({ value }) =>
    setDraft((prev) => ({
      ...prev,
      app: value,
      language: handleResolveLanguage({
        app: value,
        language: prev.picks[value] || prev.language,
      }),
    }));

  const handleSelectLanguage = ({ value }) =>
    setDraft((prev) => ({
      ...prev,
      language: value,
      picks: { ...prev.picks, [prev.app]: value },
    }));

  const handleConfirm = () => {
    if (isDirty) {
      handleSwitchAndNavigate({ app: draft.app, language: draft.language });
    }
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      handleConfirmation={handleConfirm}
      isButtonsRequired={false}
      className="md:!w-[640px] lg:!w-[640px] !rounded-2xl overflow-hidden"
      bodyClassName="!p-0"
    >
      <div className="flex items-start justify-between gap-4 px-7 pb-2 pt-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Switch app</h2>
          <p className="mt-1 text-sm text-gray-500">
            App pages show requests, deliveries and launches for the app and
            language you pick.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-6 px-7 py-5">
        <div>
          <p className="mb-3 text-xs font-medium text-gray-500">App</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {appNames.map((a) => {
              const active = a.value === draft.app;
              return (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => handleSelectApp({ value: a.value })}
                  className={`relative flex flex-col items-center gap-3 rounded-2xl border px-3 pb-4 pt-9 text-center transition-all ${
                    active
                      ? "border-[#7f7ad5] bg-[#f6f5fe] ring-2 ring-[#7f7ad5]/25"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {active && (
                    <span className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#7f7ad5]">
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    </span>
                  )}
                  <img
                    src={handleGetAppLogo({ app: a.value })}
                    alt=""
                    className="h-14 w-14 rounded-xl object-cover shadow-sm ring-1 ring-black/5"
                  />
                  <span
                    className={`text-sm font-medium ${
                      active ? "text-[#3c3489]" : "text-gray-800"
                    }`}
                  >
                    {a.label}
                  </span>
                  {a.value === globalApp && (
                    <span className="absolute left-2.5 top-2.5 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                      Current
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="mb-3 text-xs font-medium text-gray-500">Language</p>
          <div className="flex flex-wrap gap-2">
            {languageOptions.map((l) => {
              const active = l.value === draft.language;
              return (
                <button
                  key={l.value}
                  type="button"
                  disabled={languageOptions.length < 2}
                  onClick={() => handleSelectLanguage({ value: l.value })}
                  className={`h-9 rounded-full px-4 text-sm transition-colors disabled:cursor-default ${
                    active
                      ? "bg-[#7f7ad5] font-medium text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-7 py-4">
        <button
          type="button"
          onClick={onClose}
          className="h-10 rounded-lg px-4 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!isDirty}
          className="h-10 rounded-lg bg-[#7f7ad5] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#6b66c4] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isDirty ? `Switch to ${handleGlobalLabel(draft)}` : "Switch"}
        </button>
      </div>
    </CustomModal>
  );
};

export default AppSwitchModal;
